import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { AlertOctagon, PauseCircle } from "lucide-react";
import LeadDetailDrawer from "@/pages/sales/LeadDetailDrawer";

export default function ReviewQueuePage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [executives, setExecutives] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [r, e] = await Promise.all([api.get("/sales/review-queue"), api.get("/sales/executives")]);
      setLeads(r.data);
      setExecutives(e.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const lossLeads = leads.filter(l => l.status === "loss");
  const holdLeads = leads.filter(l => l.status === "hold");

  return (
    <div className="min-h-full">
      <div className="border-b border-[#262626] px-10 py-6 bg-[#050505]/80 backdrop-blur-xl sticky top-0 z-30">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Sales · Strategic</p>
        <h1 className="font-display text-3xl text-white mt-1">Review Queue</h1>
        <p className="text-xs text-gray-500 font-mono mt-1">Deeper look at leads marked Loss or Hold. Evaluate remarks and re-plan.</p>
      </div>

      <div className="px-10 py-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {[
          { title: "Loss", icon: AlertOctagon, list: lossLeads, color: "text-red-400", border: "border-red-500/30", testid: "review-loss" },
          { title: "Hold / Nurture", icon: PauseCircle, list: holdLeads, color: "text-[#a78bfa]", border: "border-[#a78bfa]/30", testid: "review-hold" },
        ].map((b) => {
          const Icon = b.icon;
          return (
            <div key={b.title} className="iz-card p-5" data-testid={b.testid}>
              <div className="flex items-center gap-3 mb-4 pb-3 border-b border-[#262626]">
                <div className={`w-9 h-9 rounded-full border ${b.border} flex items-center justify-center`}>
                  <Icon className={`w-4 h-4 ${b.color}`} strokeWidth={1.5}/>
                </div>
                <div>
                  <p className={`text-[10px] font-mono uppercase tracking-[0.3em] ${b.color}`}>{b.title}</p>
                  <p className="font-display text-lg text-white">{b.list.length} lead{b.list.length !== 1 ? "s" : ""}</p>
                </div>
              </div>
              {loading ? (
                <p className="text-xs text-gray-500 text-center py-6">Loading…</p>
              ) : b.list.length === 0 ? (
                <p className="text-xs text-gray-500 text-center py-6">No leads in this bucket.</p>
              ) : (
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {b.list.map(l => (
                    <button key={l.id} onClick={() => setSelected(l)} className="w-full text-left p-3 rounded-md border border-[#262626] hover:border-[#d4af37]/40 transition-colors" data-testid={`review-card-${l.id}`}>
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-white">{l.client_name}</p>
                        <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">{l.phone}</span>
                      </div>
                      {l.loss_data && (
                        <p className="text-xs text-red-400 mt-1">Reason: <span className="text-gray-300">{l.loss_data.reason}</span></p>
                      )}
                      {l.loss_data?.remarks && <p className="text-[11px] text-gray-500 italic mt-0.5">"{l.loss_data.remarks}"</p>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <LeadDetailDrawer lead={selected} onClose={() => setSelected(null)} onChanged={load} executives={executives}/>
    </div>
  );
}
